import { useEffect, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { MemberLookupResult, CategoryStatus } from "@/hooks/monitor/useMemberLookup";
import { User } from "lucide-react";

interface MemberResultModalProps {
  open: boolean;
  onClose: () => void;
  result: MemberLookupResult | null;
}

const AUTO_CLOSE_SECONDS = 5;

// Component to display category status
const CategoryStatusItem = ({ category }: { category: CategoryStatus }) => {
  const isExpired = category.isExpired;
  const daysAbs = Math.abs(category.daysRemaining);
  
  return (
    <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
      <span className="text-xl text-white/90 font-medium truncate mr-4">
        {category.name}
      </span>
      <span className={`text-xl font-semibold whitespace-nowrap ${
        isExpired 
          ? 'text-red-300' 
          : category.daysRemaining <= 5 
            ? 'text-yellow-300' 
            : 'text-green-300'
      }`}>
        {isExpired 
          ? `Vencido (${daysAbs}d)`
          : category.daysRemaining === 0 
            ? 'Vence hoy'
            : category.daysRemaining === 1 
              ? 'Vence mañana'
              : `${category.daysRemaining} días`
        }
      </span>
    </div>
  );
};

export const MemberResultModal = ({ open, onClose, result }: MemberResultModalProps) => {
  const [timeLeft, setTimeLeft] = useState(AUTO_CLOSE_SECONDS);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showContent, setShowContent] = useState(false);

  // Handle open/close animations
  useEffect(() => {
    if (open && result) {
      setIsExpanded(true);
      setIsClosing(false);
      // Delay content appearance
      const contentTimer = setTimeout(() => setShowContent(true), 300);
      return () => clearTimeout(contentTimer);
    }
  }, [open, result]);

  // Auto-close timer
  useEffect(() => {
    if (!open || !isExpanded) {
      setTimeLeft(AUTO_CLOSE_SECONDS);
      return;
    }

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleClose();
          return AUTO_CLOSE_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open, isExpanded]);

  const handleClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    setShowContent(false);
    
    // Wait for content to fade out, then contract circle
    setTimeout(() => {
      setIsExpanded(false);
    }, 200);
    
    // Wait for circle to contract, then call onClose
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setTimeLeft(AUTO_CLOSE_SECONDS);
    }, 700);
  };

  if (!result && !open) return null;

  const { member, paymentStatus, isPending, isAmarelo, categoryStatuses, hasInscripcion } = result || { 
    member: null, 
    paymentStatus: null, 
    isPending: false,
    isAmarelo: false,
    categoryStatuses: [],
    hasInscripcion: false
  };
  const fullName = member ? [member.first_name, member.last_name].filter(Boolean).join(" ") || "Sin nombre" : "";

  const getBackgroundColor = () => {
    switch (paymentStatus?.color) {
      case 'green':
        return "bg-green-600";
      case 'yellow':
        return "bg-yellow-500";
      case 'red':
        return "bg-red-600";
      default:
        return "bg-background";
    }
  };

  // Don't render if not open and not animating out
  if (!open && !isClosing) return null;

  return (
    <div 
      className="fixed inset-0 z-50 overflow-hidden"
      onClick={handleClose}
    >
      {/* Expanding circle background */}
      <div 
        className={`
          absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
          rounded-full ${getBackgroundColor()}
          transition-all duration-500 ease-out
        `}
        style={{
          width: isExpanded ? '250vmax' : '0px',
          height: isExpanded ? '250vmax' : '0px',
        }}
      />

      {/* Progress bar */}
      <div 
        className={`
          absolute top-0 left-0 right-0 h-2 bg-white/20 z-10
          transition-opacity duration-300
          ${showContent ? 'opacity-100' : 'opacity-0'}
        `}
      >
        <div 
          className="h-full bg-white transition-all duration-1000 ease-linear"
          style={{ width: `${(timeLeft / AUTO_CLOSE_SECONDS) * 100}%` }}
        />
      </div>

      {/* Content */}
      <div 
        className={`
          absolute inset-0 flex flex-col items-center justify-center
          transition-opacity duration-300
          ${showContent ? 'opacity-100' : 'opacity-0'}
        `}
        style={{ pointerEvents: showContent ? 'auto' : 'none' }}
      >
        {member && paymentStatus && (
          <div className="flex flex-col items-center text-center space-y-6 p-8 max-w-2xl w-full">
            {/* Avatar */}
            <Avatar className="h-32 w-32 ring-4 ring-white/20">
              <AvatarImage src={member.avatar_url || undefined} alt={fullName} className="object-cover" />
              <AvatarFallback className="text-4xl bg-white/10">
                <User className="h-16 w-16 text-white" />
              </AvatarFallback>
            </Avatar>

            {/* Name */}
            <h2 className="text-5xl font-bold tracking-tight text-white uppercase">
              {fullName}
            </h2>

            {/* Pending Badge */}
            {isPending && (
              <Badge variant="outline" className="text-lg px-4 py-2 border-white/30 text-white">
                Pendiente de registro
              </Badge>
            )}

            {/* Payment Status */}
            <div className="w-full max-w-lg p-8 rounded-3xl bg-white/10 backdrop-blur-sm border border-white/20">
              <div className="text-6xl font-bold text-white uppercase tracking-wide">
                {paymentStatus.label}
              </div>
              {paymentStatus.daysRemaining !== undefined && paymentStatus.status !== 'expired' && (
                <div className="text-3xl text-white/90 mt-4 font-semibold">
                  {paymentStatus.daysRemaining === 0 
                    ? "Vence hoy"
                    : paymentStatus.daysRemaining === 1
                      ? "Vence mañana"
                      : `${paymentStatus.daysRemaining} días restantes`
                  }
                </div>
              )}

              {/* Amarelo-specific: Category statuses inside the card */}
              {isAmarelo && categoryStatuses && categoryStatuses.filter(c => c.name !== 'Inscripción').length > 0 && (
                <div className="space-y-2 mt-6 pt-6 border-t border-white/20">
                  {categoryStatuses
                    .filter(c => c.name !== 'Inscripción')
                    .map((category, idx) => (
                      <CategoryStatusItem key={idx} category={category} />
                    ))}
                </div>
              )}
            </div>

            <p className="text-lg text-white/70">
              Toca en cualquier lugar para cerrar • {timeLeft}s
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
